import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, Clock, Users, Trophy, TrendingUp, Sparkles } from 'lucide-react';

/**
 * Animated Number Component - Slot machine effect
 */
const AnimatedNumber = ({ value, suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValue = useRef(value);
  
  useEffect(() => {
    const diff = value - previousValue.current;
    if (diff === 0) return;
    
    const steps = 20;
    const increment = diff / steps;
    let current = previousValue.current;
    let step = 0;
    
    const animate = () => {
      step++;
      current += increment;
      setDisplayValue(Math.round(current));
      
      if (step < steps) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
        previousValue.current = value;
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);
  
  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  return (
    <span className="tabular-nums">
      {formatNumber(displayValue)}{suffix}
    </span>
  );
};

/**
 * Live Stats Counter - SLOT MACHINE EFFECT
 * - Numbers animate like slot machines
 * - Particle explosions on milestones
 * - Real-time feel with random increments
 */
const LiveStatsCounter = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    kgLost: 11234,
    hoursJejum: 79543,
    activeMembers: 4687,
    goalsAchieved: 1623,
  });
  const [particles, setParticles] = useState([]);
  const containerRef = useRef(null);
  
  // Animate stats with slot machine effect
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => {
        const newStats = {
          kgLost: prev.kgLost + Math.floor(Math.random() * 3),
          hoursJejum: prev.hoursJejum + Math.floor(Math.random() * 10),
          activeMembers: prev.activeMembers + (Math.random() > 0.7 ? 1 : 0),
          goalsAchieved: prev.goalsAchieved + (Math.random() > 0.8 ? 1 : 0),
        };
        
        // Check for milestones
        if (Math.floor(newStats.kgLost / 100) > Math.floor(prev.kgLost / 100)) {
          createParticleExplosion();
        }
        
        return newStats;
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Create particle explosion
  const createParticleExplosion = () => {
    const newParticles = [];
    for (let i = 0; i < 20; i++) {
      newParticles.push({
        id: Date.now() + i,
        x: 50 + Math.random() * 20 - 10,
        y: 50 + Math.random() * 20 - 10,
        vx: (Math.random() - 0.5) * 10,
        vy: (Math.random() - 0.5) * 10,
        life: 1,
        size: Math.random() * 6 + 2,
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
    
    // Remove particles after animation
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
    }, 1500);
  };
  
  const statItems = [
    { key: 'kgLost', value: stats.kgLost, label: t('landing.stats.kgLost'), icon: Scale, suffix: ' kg' },
    { key: 'hoursJejum', value: stats.hoursJejum, label: t('landing.stats.fastingHours'), icon: Clock, suffix: ' h' },
    { key: 'activeMembers', value: stats.activeMembers, label: t('landing.stats.activeMembers'), icon: Users, suffix: '' },
    { key: 'goalsAchieved', value: stats.goalsAchieved, label: t('landing.stats.goalsAchieved'), icon: Trophy, suffix: '' },
  ];
  
  // Today's activity - use useState to avoid impure function in render
  const [todayJoined] = useState(() => Math.floor(Math.random() * 15) + 18);
  
  return (
    <div ref={containerRef} className="relative w-full max-w-5xl mx-auto px-4 py-8 bg-black rounded-3xl"
         style={{ backgroundColor: '#0a0a0a' }}>
      {/* Particle effects */}
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-[#a3e635] pointer-events-none"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            opacity: p.life,
            transform: `translate(${p.vx * 20}px, ${p.vy * 20}px)`,
            transition: 'all 1.5s ease-out',
            boxShadow: '0 0 10px #a3e635',
          }}
        />
      ))}
      
      {/* Section header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a3e635]/10 border border-[#a3e635]/30 mb-4">
          <TrendingUp className="w-4 h-4 text-[#a3e635]" />
          <span className="text-[#a3e635] text-sm font-medium">LIVE STATS</span>
          <div className="w-2 h-2 rounded-full bg-[#a3e635] animate-pulse" />
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
          {t('landing.liveStats.communityInAction')} <span className="text-[#a3e635]">{t('landing.liveStats.community')}</span>
        </h3>
        <p className="text-gray-400 text-sm">
          {t('landing.liveStats.realResultsRealTime')}
        </p>
      </div>
      
      {/* Stats grid - BLACK BACKGROUND with VIBRANT GREEN */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {statItems.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.key}
              className="relative group p-4 sm:p-6 rounded-2xl bg-black border-2 border-[#a3e635]/50
                       hover:border-[#a3e635] transition-all duration-300
                       shadow-[0_0_20px_rgba(163,230,53,0.15)] hover:shadow-[0_0_40px_rgba(163,230,53,0.4)]"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon with glow */}
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3
                            rounded-xl bg-black border-2 border-[#a3e635]/60
                            shadow-[0_0_15px_rgba(163,230,53,0.3)]
                            group-hover:shadow-[0_0_25px_rgba(163,230,53,0.5)] transition-all">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#a3e635] drop-shadow-[0_0_8px_rgba(163,230,53,0.8)]" />
              </div>
              
              {/* Value with VIBRANT GREEN glow */}
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-[#a3e635]
                              drop-shadow-[0_0_15px_rgba(163,230,53,0.8)]">
                  <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-xs sm:text-sm text-gray-400 mt-1 uppercase tracking-wide">
                  {stat.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Today's activity badge */}
      <div className="flex justify-center mt-8">
        <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full 
                      bg-gradient-to-r from-[#a3e635]/20 to-[#39FF14]/10
                      border border-[#a3e635]/40 shadow-[0_0_20px_rgba(163,230,53,0.2)]">
          <Sparkles className="w-4 h-4 text-[#a3e635] animate-pulse" />
          <span className="text-white text-sm font-medium">
            +{todayJoined} {t('landing.stats.joinedToday')}
          </span>
          <div className="w-1.5 h-1.5 rounded-full bg-[#a3e635] animate-ping" />
        </div>
      </div>
    </div>
  );
};

export default LiveStatsCounter;
